import { Injectable } from "@angular/core";

/**
 * A utility service that provides static methods for immutable operations on collections.
 *
 * ReducerUtilsService offers common helper functions to perform state updates such as setting,
 * resetting, adding, updating, and deleting items from arrays. These methods are designed for use
 * in reducer functions and other contexts where immutable data updates are required.
 */
@Injectable({
  providedIn: "root",
})
export class ReducerUtilsService {
  /**
   * Replaces the current collection with a new one.
   *
   * @template T - The type of items in the collection.
   * @param {T[]} items - The new collection of items.
   * @returns {T[]} The new collection.
   */
  static set<T>(items: T[]): T[] {
    return items;
  }

  /**
   * Resets the collection.
   *
   * @template T - The type of items in the collection.
   * @returns {(T[] | undefined)} Undefined, indicating that the collection is reset.
   */
  static reset<T>(): T[] | undefined {
    return undefined;
  }

  /**
   * Adds a single item to the collection.
   *
   * @template T - The type of items in the collection.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {T} newItem - The item to add.
   * @returns {T[]} The collection with the new item added.
   */
  static addItem<T>(items: T[] | undefined, newItem: T): T[] {
    return [...(items ?? []), newItem];
  }

  /**
   * Adds multiple items to the collection.
   *
   * @template T - The type of items in the collection.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {T[]} newItems - The items to add.
   * @returns {T[]} The collection with the new items added.
   */
  static addItems<T>(items: T[] | undefined, newItems: T[]): T[] {
    return [...(items ?? []), ...newItems];
  }

  /**
   * Updates an item in the collection.
   * If an item with the same id exists, it is removed before adding the updated item.
   *
   * @template T - The type of items which extend an object with an `id` property.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {T} updatedItem - The item to update.
   * @returns {T[]} The updated collection.
   */
  static updateItem<T extends { id: number | string }>(items: T[] | undefined, updatedItem: T): T[] {
    if (items) {
      return [...items.filter((item) => item.id !== updatedItem.id), updatedItem];
    }
    return [updatedItem];
  }

  /**
   * Updates multiple items in the collection.
   * Any existing items with matching ids are removed before adding the updated items.
   *
   * @template T - The type of items which extend an object with an `id` property.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {T[]} updatedItems - The items to update.
   * @returns {T[]} The updated collection.
   */
  static updateItems<T extends { id: number | string }>(items: T[] | undefined, updatedItems: T[]): T[] {
    if (items) {
      const updatedIds = updatedItems.map((item) => item.id);
      return [...items.filter((item) => !updatedIds.includes(item.id)), ...updatedItems];
    }
    return [...updatedItems];
  }

  /**
   * Deletes an item from the collection by its id.
   *
   * @template T - The type of items which extend an object with an `id` property.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {number | string} id - The id of the item to delete.
   * @returns {T[]} The collection with the item removed.
   */
  static deleteItem<T extends { id: number | string }>(items: T[] | undefined, id: number | string): T[] {
    return items ? items.filter((item) => item.id !== id) : [];
  }

  /**
   * Deletes multiple items from the collection by their ids.
   *
   * @template T - The type of items which extend an object with an `id` property.
   * @param {(T[] | undefined)} items - The current collection, or undefined if empty.
   * @param {number | string[]} ids - The ids of the items to delete.
   * @returns {T[]} The collection with the items removed.
   */
  static deleteItems<T extends { id: number | string }>(items: T[] | undefined, ids: (number | string)[]): T[] {
    return items ? items.filter((item) => !ids.includes(item.id)) : [];
  }
}
